import Select from 'src/modules/shared/components/atoms/inputs/select';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { PiInfo } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TemplateComposerHeadingProps } from '../../../types/templates/template-composer.types';
import {
  templateDisplayName,
  templateLanguageLabel,
  templateCategoryLabel,
} from '../../../utils/template-composer.util';

/**
 * Identifies the selected template and offers only actual language versions.
 * @param props - Selected definition, account-scoped versions and selection action.
 * @returns A title with metadata and a language control when alternatives exist.
 */
export const TemplateComposerHeading = ({
  template,
  variants,
  onSelect,
}: TemplateComposerHeadingProps) => {
  const { t, language } = useI18n();
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <Text as="h3" weight="semibold" className="min-w-0 break-words">
            {templateDisplayName(template.name)}
          </Text>
          <Tooltip
            interactive
            as="span"
            placement="bottom"
            size="sm"
            className="max-w-[min(20rem,calc(100vw-2rem))]"
            content={
              <dl className="space-y-2">
                <div>
                  <Text as="dt" size="tiny" color="inherit">
                    {t('whatsappTemplates.technicalName')}
                  </Text>
                  <Text
                    as="dd"
                    size="tiny"
                    color="inherit"
                    className="break-all font-mono"
                  >
                    {template.name}
                  </Text>
                </div>
                <div>
                  <Text as="dt" size="tiny" color="inherit">
                    {t('whatsappTemplates.status')}
                  </Text>
                  <Text as="dd" size="tiny" color="inherit">
                    {template.status}
                  </Text>
                </div>
              </dl>
            }
          >
            <Button
              type="button"
              appearance="ghost"
              variant="neutral"
              size="xs"
              isIconOnly
              aria-label={t('whatsappTemplates.details')}
            >
              <PiInfo className="h-4 w-4" aria-hidden />
            </Button>
          </Tooltip>
        </div>
        <Text size="tiny" color="secondary" className="mt-1">
          {template.status.toUpperCase() === 'APPROVED'
            ? t('whatsappTemplates.approved')
            : template.status}
          {' · '}
          {templateCategoryLabel(template.category, t)}
          {variants.length <= 1 &&
            ` · ${templateLanguageLabel(template.language, language)}`}
        </Text>
      </div>
      {variants.length > 1 && (
        <div className="w-40">
          <Select
            aria-label={t('whatsappTemplates.language')}
            value={template.id}
            onChange={(event) => onSelect(event.target.value)}
            className="!text-base"
            options={variants.map((variant) => ({
              value: variant.id,
              content: templateLanguageLabel(variant.language, language),
            }))}
          />
        </div>
      )}
    </div>
  );
};
